import cn from 'classnames';
import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';

import { IconButton } from 'components';
import { ButtonV3, DropdownV3, InputV2 } from 'components/ComponentV2';
import { Modal } from 'components/Modal/Modal.component';
import { Row } from 'components/layout';
import { DestroyAttributes } from 'hooks/useCandidateProfile';
import { useCandidateProfile } from 'lib/contexts/candidate-profile-context';
import { AccomplishmentResponseAttributes } from 'lib/models/candidate';
import { Option } from 'lib/models/option';
import { useAuth } from 'lib/providers/AuthProvider';

import styles from '../ProfileModal.common.module.scss';
import DisplaySkills from './DisplaySkills';
import { getSkillsInfo, getUpdateAccomplishmentValue } from './utils';

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AddSkillsModal = memo(({ isOpen, onClose }: ProfileModalProps) => {
  const [accomplishmentValue, setAccomplishmentValue] = useState<string>('');

  const { t } = useTranslation('candidate.profile.modal');

  const [typeDropdownValue, setTypeDropdownValue] = useState<string>(
    t('skills.dropdown-options.skills')
  );

  const skillDropdownOption: Array<Option> = [
    {
      id: '1',
      label: t('skills.dropdown-options.skills'),
      value: t('skills.dropdown-options.skills'),
    },
    {
      id: '2',
      label: t('skills.dropdown-options.achievements'),
      value: t('skills.dropdown-options.achievements'),
    },
    {
      id: '3',
      label: t('skills.dropdown-options.interests'),
      value: t('skills.dropdown-options.interests'),
    },
  ];
  const { candidate } = useAuth();

  const [accomplishmentsInfo, setAccomplishmentsInfo] = useState<
    (AccomplishmentResponseAttributes & {
      isNew: boolean;
    })[]
  >([]);
  const [payload, setPayload] = useState<AccomplishmentResponseAttributes[]>(
    []
  );

  useEffect(() => {
    if (candidate) {
      setAccomplishmentsInfo(getSkillsInfo(candidate));
    }
  }, [candidate]);

  const { updateCandidate } = useCandidateProfile();

  const getPlaceholderType = useMemo(() => {
    switch (typeDropdownValue) {
      case t('skills.dropdown-options.skills'):
        return t('skills.skill');
      case t('skills.dropdown-options.achievements'):
        return t('skills.achievement');
      case t('skills.dropdown-options.interests'):
        return t('skills.interest');
      default:
        return '';
    }
  }, [typeDropdownValue]);

  const updatePayload = () => {
    const newPayload: AccomplishmentResponseAttributes[] = [];
    accomplishmentsInfo.map((item) => {
      const { id, title, values, isNew } = item;
      const accomplishments = getUpdateAccomplishmentValue({
        id,
        title,
        values,
        isNew,
      });
      newPayload.push(accomplishments.accomplishments_attributes[0]);
    });
    setPayload(newPayload);
  };

  useEffect(() => {
    updatePayload();
  }, [accomplishmentsInfo]);

  const saveValue = () => {
    let index = 0;
    const existingAccomplishment = payload.filter((item, i) => {
      if (item.title === typeDropdownValue) {
        index = i;
        return true;
      }
    });

    const newPayload = structuredClone(payload);
    if (existingAccomplishment.length) {
      newPayload[index].values.push(accomplishmentValue);
    } else {
      const accomplishment = {
        id: '',
        title: typeDropdownValue,
        values: [accomplishmentValue],
        isNew: true,
      };
      const newAccomplishment = getUpdateAccomplishmentValue(accomplishment);
      newPayload.push(newAccomplishment.accomplishments_attributes[0]);
    }

    setPayload(newPayload);
    setAccomplishmentValue('');
  };

  const updateCandidateData = async () => {
    const candidate: {
      accomplishments_attributes: (
        | AccomplishmentResponseAttributes
        | DestroyAttributes
      )[];
    } = {
      accomplishments_attributes: [],
    };

    const updatedAccomplishment = payload.filter(
      (item) => item.values.length !== 0
    );
    candidate.accomplishments_attributes.push(...updatedAccomplishment);

    const emptyAccomplishment = payload.filter(
      (item) => item.values.length === 0
    );
    emptyAccomplishment.map((item) => {
      if (item.id) {
        const newPayload: DestroyAttributes = {
          id: item.id,
          _destroy: true,
        };
        candidate.accomplishments_attributes.push(newPayload);
      }
    });

    await updateCandidate(candidate);
    onClose();
  };

  const closeModal = () => {
    updatePayload();
    onClose();
  };

  const isEdit = useMemo(() => {
    let isEdit = false;
    const getAllType = skillDropdownOption.map((item) => item.label);
    if (accomplishmentsInfo.length === 0) {
      isEdit = false;
    }
    const accomplishments = accomplishmentsInfo.filter((item) =>
      getAllType.includes(item.title)
    );
    accomplishments.forEach((item) => {
      if (item.values.length > 0) {
        isEdit = true;
      }
    });

    return isEdit;
  }, [accomplishmentsInfo]);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      contentClassName={styles.ProfileModal}
    >
      <div className={styles.ProfileModalHeader}>
        {isEdit ? <p>{t('skills.edit')}</p> : <p>{t('skills.add')}</p>}
        <IconButton
          onClick={closeModal}
          className={styles.ProfileModalHeaderClose}
          iconName="close"
          size="small"
        />
        <IconButton
          onClick={closeModal}
          className={styles.ProfileModalHeaderCloseMobile}
          iconName="arrow-button-left"
          size="small"
        />
      </div>
      <div className={cn(styles.ProfileModalContent)}>
        <Row className={cn(styles.inputRow, styles.alignBottom)}>
          <DropdownV3
            label={t('skills.type.label')}
            title={t('skills.type.label')}
            value={typeDropdownValue}
            onChange={(value) => setTypeDropdownValue(value)}
            options={skillDropdownOption}
            selectedValueLabel={typeDropdownValue}
            isValid={true}
            isSearchable={false}
            className={cn(styles.input, styles.width35)}
          />

          <InputV2
            maxLength={120}
            label=""
            placeholder={t('skills.placeholder', {
              type: getPlaceholderType,
            })}
            value={accomplishmentValue}
            onChange={(event) => {
              setAccomplishmentValue(event.target.value);
            }}
            onKeyPress={(event) => {
              if (event.key === 'Enter') {
                saveValue();
              }
            }}
            className={cn(styles.input, styles.width65)}
          />
        </Row>
        <DisplaySkills
          isEdit={true}
          payload={payload}
          setPayload={setPayload}
        />
      </div>
      <div className={cn(styles.ProfileModalAction, styles.singleCta)}>
        <ButtonV3 onClick={updateCandidateData} className={styles.button}>
          {t('common.done')}
        </ButtonV3>
      </div>
    </Modal>
  );
});

AddSkillsModal.displayName = 'AddSkillsModal';
